import * as React from "react";
import { useLocation } from "react-router-dom";

// Get current user ID for user-specific history
const getCurrentUserId = (): string => {
  try {
    // Try to get user ID from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      // You can decode JWT token here to get user ID
      // For now, using a simple approach
      return localStorage.getItem('userId') || 'anonymous';
    }
    return 'anonymous';
  } catch (error) {
    return 'anonymous';
  }
};

// Get user-specific storage key
const getUserHistoryKey = (userId: string): string => {
  return `navigationHistory_${userId}`;
};

// Utility functions for manual history management
export const addToNavigationHistory = (path: string, limit: number = 5) => {
  try {
    const userId = getCurrentUserId();
    const storageKey = getUserHistoryKey(userId);
    
    const savedHistory = localStorage.getItem(storageKey);
    let history = savedHistory ? JSON.parse(savedHistory) : [];
    
    // Add new path if it's different from the last one
    if (history.length === 0 || history[history.length - 1] !== path) {
      history.push(path);
    }
    
    // Limit history size
    if (history.length > limit) {
      history = history.slice(-limit);
    }
    
    localStorage.setItem(storageKey, JSON.stringify(history));
    console.log(`Added to history for user ${userId}:`, path, 'Current history:', history);
    return history;
  } catch (error) {
    console.warn('Failed to add to navigation history:', error);
    return [];
  }
};

export const getNavigationHistory = (): string[] => {
  try {
    const userId = getCurrentUserId();
    const storageKey = getUserHistoryKey(userId);
    
    const savedHistory = localStorage.getItem(storageKey);
    const history = savedHistory ? JSON.parse(savedHistory) : [];
    console.log(`Retrieved history for user ${userId}:`, history);
    return history;
  } catch (error) {
    console.warn('Failed to get navigation history:', error);
    return [];
  }
};

export const clearNavigationHistory = () => {
  try {
    const userId = getCurrentUserId();
    const storageKey = getUserHistoryKey(userId);
    
    localStorage.removeItem(storageKey);
    console.log(`Navigation history cleared for user ${userId}`);
  } catch (error) {
    console.warn('Failed to clear navigation history:', error);
  }
};

// Clear all users' history (admin function)
export const clearAllUsersHistory = () => {
  try {
    // Clear all navigation history keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('navigationHistory_')) {
        localStorage.removeItem(key);
        console.log(`Cleared history for key: ${key}`);
      }
    });
    console.log('All users navigation history cleared');
  } catch (error) {
    console.warn('Failed to clear all users history:', error);
  }
};

// Initialize with some default paths if localStorage is empty
const initializeDefaultHistory = (userId: string) => {
  try {
    const storageKey = getUserHistoryKey(userId);
    const savedHistory = localStorage.getItem(storageKey);
    
    if (!savedHistory) {
      // Add some realistic default paths that a user might have visited
      const defaultHistory = ['/', '/dashboard', '/dashboard/coaches', '/dashboard/analytics', '/login'];
      localStorage.setItem(storageKey, JSON.stringify(defaultHistory));
      console.log(`Initialized default history for user ${userId}:`, defaultHistory);
      return defaultHistory;
    }
    return JSON.parse(savedHistory);
  } catch (error) {
    console.warn('Failed to initialize default history:', error);
    return [];
  }
};

export function useNavigationHistory(limit: number = 5, persistHistory: boolean = true) {
  const location = useLocation();
  const historyRef = React.useRef<string[]>([]);
  const isInitialized = React.useRef(false);
  const currentUserId = React.useRef<string>('');

  React.useEffect(() => {
    // Get current user ID
    const userId = getCurrentUserId();
    currentUserId.current = userId;
    
    // Initialize history on first load
    if (!isInitialized.current) {
      isInitialized.current = true;
      
      // Try to restore history from localStorage if persistence is enabled
      if (persistHistory) {
        try {
          const storageKey = getUserHistoryKey(userId);
          const savedHistory = localStorage.getItem(storageKey);
          
          if (savedHistory) {
            historyRef.current = JSON.parse(savedHistory);
            console.log(`Restored history for user ${userId}:`, historyRef.current);
          } else {
            // Initialize with default history if none exists
            historyRef.current = initializeDefaultHistory(userId);
          }
        } catch (error) {
          console.warn('Failed to restore navigation history:', error);
          historyRef.current = initializeDefaultHistory(userId);
        }
      }
      
      // Always add current path to history if it's different
      if (historyRef.current.length === 0 || historyRef.current[historyRef.current.length - 1] !== location.pathname) {
        historyRef.current.push(location.pathname);
        console.log(`Added initial path to history for user ${userId}:`, location.pathname);
      }
    } else {
      // Add new path to history if it's different
      if (historyRef.current[historyRef.current.length - 1] !== location.pathname) {
        historyRef.current.push(location.pathname);
        console.log(`Added new path to history for user ${userId}:`, location.pathname);
      }
    }

    // Limit history size
    if (historyRef.current.length > limit) {
      const removed = historyRef.current.shift();
      console.log(`Removed old path from history for user ${userId}:`, removed);
    }

    // Save to localStorage if persistence is enabled
    if (persistHistory) {
      try {
        const storageKey = getUserHistoryKey(userId);
        localStorage.setItem(storageKey, JSON.stringify(historyRef.current));
        console.log(`Saved history to localStorage for user ${userId}:`, historyRef.current);
      } catch (error) {
        console.warn('Failed to save navigation history:', error);
      }
    }
  }, [location, limit, persistHistory]);

  // Debug: Log current history whenever it changes
  React.useEffect(() => {
    console.log(`useNavigationHistory hook - Current history for user ${currentUserId.current}:`, historyRef.current);
  }, [historyRef.current]);

  return historyRef.current;
}



// WEBPACK FOOTER //
// ./src/hooks/useNavigationHistory.ts